import { Building2, Lock, Phone, ShieldCheck, SlidersHorizontal, Stethoscope } from "lucide-react";
import { Navigate, useParams } from "react-router";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { CONFIG_LAYOUT_TOUR_STEPS, CONFIG_NOTIFICACOES_TOUR_STEPS, PERFIL_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { PaginaComSecoes, type Secao } from "@/components/layout/PaginaComSecoes";
import { CabecalhoDoPerfil } from "@/components/conta/CabecalhoDoPerfil";
import { DadosProfissionaisSecao } from "@/components/conta/DadosProfissionaisSecao";
import { OrganizacoesSecao } from "@/components/conta/OrganizacoesSecao";
import { ContatoSecao } from "@/components/conta/ContatoSecao";
import { SegurancaSecao } from "@/components/conta/SegurancaSecao";
import { PrivacidadeSecao } from "@/components/conta/PrivacidadeSecao";
import { SettingsNotifications } from "./SettingsNotifications";
import { SettingsLayout } from "./SettingsLayout";

/** Avisos e aparência numa seção só, um embaixo do outro. */
function PreferenciasSecao() {
  return (
    <div className="space-y-6">
      <SettingsLayout embutido />
      <SettingsNotifications embutido />
    </div>
  );
}

const SECOES: Secao[] = [
  { chave: "perfil", grupo: "Você", rotulo: "Dados profissionais", descricao: "Nome, CRO, especialidades e biografia", icone: <Stethoscope className="h-4 w-4" />, cor: "#2457ff", conteudo: <DadosProfissionaisSecao /> },
  { chave: "organizacoes", grupo: "Você", rotulo: "Organizações", descricao: "Onde você trabalha e com que papel", icone: <Building2 className="h-4 w-4" />, cor: "#0ea5e9", conteudo: <OrganizacoesSecao /> },
  { chave: "contato", grupo: "Você", rotulo: "Contato", descricao: "Telefone, CPF e e-mails", icone: <Phone className="h-4 w-4" />, cor: "#10b981", conteudo: <ContatoSecao /> },
  { chave: "seguranca", grupo: "Você", rotulo: "Segurança", descricao: "Senha de acesso e PIN de sócio", icone: <Lock className="h-4 w-4" />, cor: "#b45309", conteudo: <SegurancaSecao /> },
  { chave: "preferencias", grupo: "Você", rotulo: "Preferências", descricao: "Avisos, sons e estilo do menu", icone: <SlidersHorizontal className="h-4 w-4" />, cor: "#6366f1", conteudo: <PreferenciasSecao />, semMoldura: true },
  { chave: "privacidade", grupo: "Você", rotulo: "Privacidade e dados", descricao: "Baixar seus dados, reset e exclusão da conta (LGPD)", icone: <ShieldCheck className="h-4 w-4" />, cor: "#64748b", conteudo: <PrivacidadeSecao /> },
];

const ANTIGAS: Record<string, string> = { profissional: "perfil", notificacoes: "preferencias", aparencia: "preferencias" };

const TOUR_POR_SECAO: Record<string, typeof PERFIL_TOUR_STEPS> = {
  perfil: PERFIL_TOUR_STEPS,
  preferencias: [...CONFIG_NOTIFICACOES_TOUR_STEPS, ...CONFIG_LAYOUT_TOUR_STEPS],
};

/** Minha conta: o que é da pessoa e vale em qualquer organização, com o perfil no topo. */
export function MinhaConta() {
  const { secao } = useParams();
  const isMobile = useIsMobile();
  const ativa = SECOES.find((s) => s.chave === secao);
  // Um tour por seção: a página de fora registra (a embutida não registra, pra não ser sobrescrita).
  useTourDaPagina(TOUR_POR_SECAO[secao ?? ""] ?? []);
  useSetPageHeader(
    {
      title: isMobile ? (ativa?.rotulo ?? "Meu perfil") : "Meu perfil",
      subtitle: isMobile ? undefined : "O que é seu e vale em todas as organizações",
    },
    [isMobile, ativa?.chave],
  );
  // Seções antigas: "Profissional" virou parte de Dados profissionais; Notificações e Aparência, de Preferências.
  const antiga = ANTIGAS[secao ?? ""];
  if (antiga) return <Navigate to={`/conta/${antiga}`} replace />;
  return <PaginaComSecoes base="/conta" titulo="Meu perfil" secoes={SECOES} topo={<CabecalhoDoPerfil />} />;
}
